import React, { Fragment } from 'react';

import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react';

import IconButton from '~/core/atoms/Buttons/IconButton';
import { FadeTransition } from '~/core/atoms/Transitions';
import CloseIcon from '~/core/iconComponents/icons/CloseIcon';
import { MaxWidth, maxWidthClasses } from '~/core/molecules/Dialog/types';
import cn from '~/utils/cn';

export interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  showCloseButton?: boolean;
  maxWidth?: MaxWidth;
}

const BaseDialog: React.FC<BaseDialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  actions,
  showCloseButton = true,
  maxWidth = 'md',
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <FadeTransition>
          <div className="fixed inset-0 bg-black/75" />
        </FadeTransition>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <FadeTransition>
              <DialogPanel
                className={cn(
                  'relative w-full transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all',
                  maxWidthClasses[maxWidth]
                )}
              >
                <DialogTitle
                  as="h3"
                  className="inline-flex w-full items-center justify-between p-8 text-2xl leading-tight font-medium text-gray-900"
                >
                  <div className="px-12">{title}</div>
                  {showCloseButton && (
                    <IconButton onClick={onClose}>
                      <CloseIcon size={20} />
                    </IconButton>
                  )}
                </DialogTitle>
                <div className="px-20 py-12">{children}</div>
                {actions && <div className="p-20">{actions}</div>}
              </DialogPanel>
            </FadeTransition>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BaseDialog;
